// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 20 / GL-6 — Growlink integration, per facility: connect (key → organization), map rooms to
// controller rooms, configure sensors/devices/photoperiod per room, request a 30-day backfill, and
// watch sync health. Mirrors AroyaIntegration; the two providers are deliberately separate (D12).

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Key, Check, X, AlertCircle, RefreshCw, Search, Settings2, Unplug, History } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Profile } from "@/types/profile";
import { supabase } from "@/integrations/supabase/client";
import { GrowlinkRoomConfigDialog, type GlConfigRoom } from "./GrowlinkRoomConfigDialog";
import { formatPhotoperiodLabel } from "@/lib/aroyaPhotoperiod";

interface Zone { id: string; zone_number: number }
interface Room { id: string; facility_id: string; room_name: string; room_type: string; growlink_room_id: string | null; photoperiod: unknown; zones: Zone[] }
interface Facility { id: string; name: string; rooms: Room[] }
interface GlRoom { id: string; name: string; roomType: number }
interface GlOrg { id: string; name: string }
interface IntegrationRow {
  provider: string; status: "pending" | "active" | "error" | "disabled"; has_secret: boolean;
  external_org_id: string | null; external_org_name: string | null;
  last_validated_at: string | null; last_ok_at: string | null; last_error: string | null;
}
interface RoomRun { room_id: string; provider: string; last_run_at: string; status: string; rows_upserted: number; rejected: number; was_empty: boolean; unmapped_sensors: string[] | null; error: string | null }
interface FacilityHealth { can_manage: boolean; integrations: IntegrationRow[]; rooms: RoomRun[] }

const UNMAPPED = "__none__";
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const fmtWhen = (iso: string | null) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
const ROOM_TYPE_LABEL: Record<number, string> = { 0: "production", 1: "non-production", 2: "fertigation" };

export function GrowlinkIntegration(_props: { profile: Profile }) {
  const { toast } = useToast();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [health, setHealth] = useState<Record<string, FacilityHealth>>({});
  const [keyDrafts, setKeyDrafts] = useState<Record<string, string>>({});
  const [orgChoices, setOrgChoices] = useState<Record<string, GlOrg[]>>({});
  const [orgDraft, setOrgDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, string | null>>({});
  const [glRooms, setGlRooms] = useState<Record<string, GlRoom[]>>({});
  const [roomDrafts, setRoomDrafts] = useState<Record<string, string>>({});
  const [savingRoom, setSavingRoom] = useState<string | null>(null);
  const [configRoom, setConfigRoom] = useState<GlConfigRoom | null>(null);

  const loadHealth = useCallback(async (ids: string[]) => {
    const entries = await Promise.all(ids.map(async (fid) => {
      const { data, error } = await (supabase as any).rpc("integration_health", { p_facility_id: fid });
      if (error) { console.error("integration_health failed", fid, error); return [fid, null] as const; }
      return [fid, data as FacilityHealth] as const;
    }));
    setHealth((prev) => { const next = { ...prev }; for (const [fid, h] of entries) if (h) next[fid] = h; return next; });
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from("facilities")
        .select("id, name, rooms ( id, facility_id, room_name, room_type, growlink_room_id, photoperiod, zones ( id, zone_number ) )")
        .order("name");
      if (error) throw error;
      const facs: Facility[] = (data ?? []).map((f: any) => ({
        id: f.id, name: f.name,
        rooms: (f.rooms ?? []).map((r: any) => ({
          id: r.id, facility_id: r.facility_id, room_name: r.room_name, room_type: r.room_type,
          growlink_room_id: r.growlink_room_id ?? null, photoperiod: r.photoperiod ?? null,
          zones: [...(r.zones ?? [])].sort((a: Zone, b: Zone) => a.zone_number - b.zone_number),
        })).sort((a: Room, b: Room) => a.room_name.localeCompare(b.room_name)),
      }));
      setFacilities(facs);
      await loadHealth(facs.map((f) => f.id));
    } catch (e) {
      console.error(e);
      toast({ variant: "destructive", title: "Error", description: "Failed to load facilities and rooms." });
    } finally {
      setIsLoading(false);
    }
  }, [loadHealth, toast]);
  useEffect(() => { load(); }, [load]);

  const row = (fid: string) => health[fid]?.integrations.find((i) => i.provider === "growlink") ?? null;
  const connected = (fid: string) => { const r = row(fid); return !!r && r.has_secret && r.status !== "disabled"; };
  const canManage = (fid: string) => health[fid]?.can_manage ?? false;
  const setB = (fid: string, v: string | null) => setBusy((b) => ({ ...b, [fid]: v }));

  const testKey = async (fid: string) => {
    const key = keyDrafts[fid]?.trim(); if (!key) return;
    setB(fid, "test");
    try {
      const { data, error } = await supabase.functions.invoke("growlink-proxy", { body: { action: "validate", facilityId: fid, apiKey: key } });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || (data?.status === 401 ? "Invalid API key" : `Controller service returned ${data?.status}`));
      const orgs: GlOrg[] = data.organizations ?? [];
      setOrgChoices((m) => ({ ...m, [fid]: orgs }));
      setOrgDraft((m) => ({ ...m, [fid]: m[fid] ?? orgs[0]?.id ?? "" }));
      toast({ title: "Key is valid", description: orgs.length ? `Found ${orgs.length} organization${orgs.length > 1 ? "s" : ""}. Choose one and click Connect.` : "The key is valid but has no organizations linked." });
    } catch (e: any) {
      setOrgChoices((m) => { const { [fid]: _, ...rest } = m; return rest; });
      toast({ variant: "destructive", title: "Connection failed", description: e.message || "Check the API key." });
    } finally { setB(fid, null); }
  };

  const connect = async (fid: string) => {
    const key = keyDrafts[fid]?.trim(); if (!key) return;
    setB(fid, "connect");
    try {
      const { data, error } = await supabase.functions.invoke("growlink-proxy", { body: { action: "connect", facilityId: fid, apiKey: key, orgId: orgDraft[fid] || undefined } });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Could not connect");
      setKeyDrafts((d) => { const { [fid]: _, ...rest } = d; return rest; });
      setOrgChoices((m) => { const { [fid]: _, ...rest } = m; return rest; });
      await loadHealth([fid]);
      toast({ title: "Connected", description: "The key was validated and stored securely for this facility." });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Error", description: e.message || "Failed to save the API key." });
    } finally { setB(fid, null); }
  };

  const disconnect = async (fid: string, name: string) => {
    if (!window.confirm(`Disconnect Growlink from ${name}? Syncing stops; historical readings are kept.`)) return;
    setB(fid, "disconnect");
    try {
      const { data, error } = await supabase.functions.invoke("growlink-proxy", { body: { action: "disconnect", facilityId: fid } });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Could not disconnect");
      await loadHealth([fid]);
      toast({ title: "Disconnected", description: `${name} no longer syncs controller data.` });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Error", description: e.message || "Failed to disconnect." });
    } finally { setB(fid, null); }
  };

  const discover = async (facility: Facility) => {
    setB(facility.id, "discover");
    try {
      const { data, error } = await supabase.functions.invoke("growlink-proxy", { body: { action: "rooms", facilityId: facility.id } });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || "Discovery failed");
      const rooms: GlRoom[] = data.data ?? [];
      setGlRooms((m) => ({ ...m, [facility.id]: rooms }));
      setRoomDrafts((prev) => {
        const next = { ...prev };
        for (const r of facility.rooms) {
          if (r.growlink_room_id || next[r.id]) continue;
          const match = rooms.find((g) => norm(g.name) === norm(r.room_name)) ?? rooms.find((g) => norm(g.name).includes(norm(r.room_name)) && norm(r.room_name).length >= 4);
          if (match) next[r.id] = match.id;
        }
        return next;
      });
      toast({ title: "Discovery complete", description: `Found ${rooms.length} controller rooms. Suggested matches filled in.` });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Discovery failed", description: e.message || "Could not list controller rooms." });
    } finally { setB(facility.id, null); }
  };

  const saveRoom = async (room: Room) => {
    const draft = roomDrafts[room.id]; if (draft == null) return;
    const value = draft === UNMAPPED || draft === "" ? null : draft.toLowerCase();
    setSavingRoom(room.id);
    try {
      const { error } = await (supabase as any).from("rooms").update({ growlink_room_id: value }).eq("id", room.id);
      if (error) throw error;
      setFacilities((facs) => facs.map((f) => ({ ...f, rooms: f.rooms.map((r) => r.id === room.id ? { ...r, growlink_room_id: value } : r) })));
      setRoomDrafts((p) => { const { [room.id]: _, ...rest } = p; return rest; });
      toast({ title: value ? "Room mapped" : "Room unmapped", description: value ? `${room.room_name} now syncs from the controller.` : `${room.room_name} no longer syncs from the controller.` });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Error", description: e.message || "Failed to save room mapping." });
    } finally { setSavingRoom(null); }
  };

  const backfill = async (room: Room) => {
    setSavingRoom(room.id);
    try {
      const { error } = await (supabase as any).rpc("growlink_request_backfill", { p_room_id: room.id, p_days: 30 });
      if (error) throw error;
      toast({ title: "Backfill queued", description: `${room.room_name}: the last 30 days will load over the next few sync cycles.` });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Backfill failed", description: e.message || "Could not queue a backfill." });
    } finally { setSavingRoom(null); }
  };

  const renderConnection = (facility: Facility) => {
    const fid = facility.id; const r = row(fid); const isOn = connected(fid); const manage = canManage(fid);
    const draft = keyDrafts[fid] ?? ""; const state = busy[fid]; const orgs = orgChoices[fid];
    const badge = !r || r.status === "disabled" || !r.has_secret ? <Badge variant="secondary">Not connected</Badge>
      : r.status === "error" ? <Badge variant="destructive">Error</Badge> : <Badge className="bg-green-600 hover:bg-green-600">Connected</Badge>;
    return (
      <div className="rounded-md border p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Key className="h-4 w-4 text-muted-foreground" /><span className="font-medium">Growlink account</span>{badge}
            {isOn && r?.external_org_name && <span className="text-xs text-muted-foreground">· {r.external_org_name}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {isOn && r?.last_validated_at ? `Key saved · validated ${fmtWhen(r.last_validated_at)}` : null}
            {isOn && r?.last_ok_at ? ` · last sync ${fmtWhen(r.last_ok_at)}` : null}
          </div>
        </div>
        {r?.status === "error" && r.last_error && (
          <Alert variant="destructive"><X className="h-4 w-4" /><AlertDescription>{r.last_error}</AlertDescription></Alert>
        )}
        {manage ? (
          <>
            <div className="space-y-2">
              <Label htmlFor={`gl-key-${fid}`}>{isOn ? "Replace API key" : "API key"}</Label>
              <div className="flex flex-wrap gap-2">
                <Input id={`gl-key-${fid}`} type="password" autoComplete="off"
                  placeholder={isOn ? "Enter a new key to rotate" : "Growlink portal → Builder → Authentication"}
                  value={draft} onChange={(e) => setKeyDrafts((d) => ({ ...d, [fid]: e.target.value }))} className="min-w-[240px] flex-1" />
                <Button variant="outline" onClick={() => testKey(fid)} disabled={!draft.trim() || !!state}>
                  {state === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Test
                </Button>
                {orgs && orgs.length > 1 && (
                  <Select value={orgDraft[fid] ?? ""} onValueChange={(v) => setOrgDraft((m) => ({ ...m, [fid]: v }))}>
                    <SelectTrigger className="w-[220px]"><SelectValue placeholder="Organization" /></SelectTrigger>
                    <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Button onClick={() => connect(fid)} disabled={!draft.trim() || !!state}>
                  {state === "connect" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{isOn ? "Rotate key" : "Connect"}
                </Button>
                {isOn && (
                  <Button variant="outline" onClick={() => disconnect(fid, facility.name)} disabled={!!state} title="Disconnect">
                    {state === "disconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                  </Button>
                )}
              </div>
            </div>
            {orgs && orgs.length === 1 && (
              <div className="text-xs text-muted-foreground"><Check className="h-3.5 w-3.5 inline-block mr-1 text-green-600" />Valid · organization "{orgs[0].name}". Click Connect to store the key.</div>
            )}
            <div className="text-xs text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 inline-block mr-1" />
              This key can control hardware in your Growlink account. It is stored encrypted for this facility only, never shown again, and Camp Promethean only reads with it.
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground">Only facility owners and admins can connect or rotate this key.</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Controller Connection &amp; Room Mapping</CardTitle>
          <CardDescription>
            Connect your Growlink account per facility, map your rooms to controller rooms, then pick which sensors and devices feed each room.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && facilities.length === 0 ? (
            <div className="flex justify-center items-center py-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /><span className="ml-3 text-muted-foreground">Loading facilities…</span></div>
          ) : facilities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No facilities found. Please create a facility first.</p>
              <Button variant="outline" onClick={() => (window.location.href = "/profile/facilities")}>Manage Facilities</Button>
            </div>
          ) : (
            <div className="space-y-10">
              {facilities.map((facility) => {
                const isOn = connected(facility.id); const manage = canManage(facility.id);
                const runs = (health[facility.id]?.rooms ?? []).filter((r) => r.provider === "growlink");
                const runByRoom = Object.fromEntries(runs.map((r) => [r.room_id, r]));
                const options = glRooms[facility.id];
                return (
                  <div key={facility.id} className="space-y-4">
                    <h3 className="text-lg font-semibold">{facility.name}</h3>
                    {renderConnection(facility)}
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">Rooms</div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => discover(facility)} disabled={busy[facility.id] === "discover" || !isOn}
                          title={isOn ? "List rooms from your Growlink account" : "Connect this facility first"}>
                          {busy[facility.id] === "discover" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Discover
                        </Button>
                        <Button variant="outline" size="sm" onClick={load} disabled={isLoading}><RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />Refresh</Button>
                      </div>
                    </div>
                    {facility.rooms.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No rooms found for this facility.</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Room</TableHead>
                            <TableHead className="w-[260px]">Controller room</TableHead>
                            <TableHead className="w-[120px]">Photoperiod</TableHead>
                            <TableHead className="w-[170px]">Last synced</TableHead>
                            <TableHead className="w-[220px] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {facility.rooms.map((room) => {
                            const draft = roomDrafts[room.id];
                            const current = room.growlink_room_id ?? UNMAPPED;
                            const value = draft ?? current;
                            const pending = draft != null && draft !== current;
                            const currentName = options?.find((g) => g.id === room.growlink_room_id)?.name;
                            const sync = runByRoom[room.id];
                            return (
                              <TableRow key={room.id}>
                                <TableCell><div className="font-medium">{room.room_name}</div><div className="text-xs text-muted-foreground">{room.room_type}</div></TableCell>
                                <TableCell>
                                  {options ? (
                                    <Select value={value} onValueChange={(v) => setRoomDrafts((p) => ({ ...p, [room.id]: v }))} disabled={!manage}>
                                      <SelectTrigger className={`w-full ${room.growlink_room_id && !pending ? "border-green-500" : pending ? "border-amber-400" : ""}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value={UNMAPPED}>Not mapped</SelectItem>
                                        {options.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} <span className="text-muted-foreground">· {ROOM_TYPE_LABEL[g.roomType] ?? ""}</span></SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{room.growlink_room_id ? (currentName ?? "Mapped") : "Not mapped"}{isOn && !room.growlink_room_id ? " · click Discover" : ""}</span>
                                  )}
                                </TableCell>
                                <TableCell><span className="text-xs tabular-nums text-muted-foreground">{formatPhotoperiodLabel(room.photoperiod)}</span></TableCell>
                                <TableCell>
                                  {sync ? (
                                    <div className="text-xs">
                                      <div className={sync.status === "error" ? "text-destructive" : "text-muted-foreground"}>{fmtWhen(sync.last_run_at)}</div>
                                      <div className="text-muted-foreground">
                                        {sync.status === "error" ? (sync.error ?? "error") : sync.was_empty ? "no sensors mapped" : `${sync.rows_upserted} rows`}
                                        {sync.rejected ? ` · ${sync.rejected} owned by another source` : ""}
                                        {sync.unmapped_sensors?.length ? ` · ${sync.unmapped_sensors.length} unmapped` : ""}
                                      </div>
                                    </div>
                                  ) : <span className="text-xs text-muted-foreground">never</span>}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    {pending && (
                                      <Button size="sm" onClick={() => saveRoom(room)} disabled={savingRoom === room.id}>
                                        {savingRoom === room.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                                      </Button>
                                    )}
                                    <Button size="sm" variant="outline" disabled={!room.growlink_room_id || !isOn || !manage} title="Pick sensors, devices + photoperiod"
                                      onClick={() => setConfigRoom({ id: room.id, facility_id: room.facility_id, room_name: room.room_name, growlink_room_id: room.growlink_room_id, photoperiod: room.photoperiod, zones: room.zones })}>
                                      <Settings2 className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" disabled={!room.growlink_room_id || !isOn || !manage || savingRoom === room.id} title="Load the last 30 days"
                                      onClick={() => backfill(room)}>
                                      <History className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <GrowlinkRoomConfigDialog room={configRoom} open={configRoom !== null} onOpenChange={(v) => { if (!v) setConfigRoom(null); }} onSaved={load} />
    </div>
  );
}
